import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { useNavigate } from "react-router-dom";
import { selectCircleError } from "../../../redux/selectors/circleSelector";
import {
  TableRow,
  TableCell,
  Stack,
  Avatar,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import { AcceptButton, DenyButton } from "../../contribute-btn/styles";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { colors } from "../../../utility/types/constant";
import { useState } from "react";
import PartialUserProfileModal from "../user/PartialUserProfileModal";
import InfoIcon from "@mui/icons-material/Info";
import {
  acceptJoinRequest,
  denyJoinRequest,
} from "../../../redux/actions/circleAction";

interface JoinRequestTableRowProps {
  profileImage: string;
  circleImage: string;
  circleName: string;
  circleId: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: string;
  accept?: any;
  deny?: any;
  selected?: any;
  isVerified?: any;
  status?: string;
  requestId?: string;
  handleOpenNotificationModal?: any;
  isNew?: boolean;
  onAcceptJoinRequest: (
    success: boolean,
    message: string,
    avatarUrl: string
  ) => any;
  onSuccess: (title: string, message: string) => any;
  onError: (title: string, message: string) => any;
  onDenyJoinRequest: (success: boolean, message: string) => any;
  reason?: string;
  rating?: number;
}

const JoinRequestTableRow = (props: JoinRequestTableRowProps) => {
  const {
    profileImage,
    circleImage,
    circleName,
    circleId,
    firstName,
    lastName,
    email,
    requestId,
    selected,
    status,
    onAcceptJoinRequest,
    onDenyJoinRequest,
    reason,
    rating,
  } = props;

  const [showPartialUserProfileModal, setShowPartialUserProfileModal] =
    useState(false);

  const fullName = `${props.firstName} ${props.lastName}`;

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const isError = useSelector(selectCircleError);

  const handleShowPartialUserProfile = () => {
    setShowPartialUserProfileModal(true);
  };

  const handleApproveJoinRequest = async () => {
    try {
      const result = await dispatch(
        acceptJoinRequest({
          circleId: circleId,
          requestId: requestId ?? "",
        })
      ).unwrap();

      if (result && result.message === "Join request approved") {
        onAcceptJoinRequest(
          true,
          `Congratulations! New member successfully joined ${circleName}.`,
          circleImage
        );
        navigate("/join-requests");
      } else {
        onAcceptJoinRequest(
          false,
          isError || "Join request was not approved.",
          circleImage
        );
      }
    } catch (error) {
      onAcceptJoinRequest(
        false,
        isError || "An unexpected error occurred. Please try again later.",
        circleImage
      );
    }
  };

  const handleDenyJoinRequest = async () => {
    try {
      const result = await dispatch(
        denyJoinRequest({
          circleId: circleId,
          requestId: requestId ?? "",
        })
      ).unwrap();

      if (result && result.message) {
        onDenyJoinRequest(true, `Member request was denied`);

        navigate("/join-requests");
      } else {
        onDenyJoinRequest(
          false,
          isError || "An unexpected error occurred. Please try again later."
        );
      }
    } catch (error) {
      onDenyJoinRequest(
        false,
        isError || "An unexpected error occurred. Please try again later."
      );
    }
  };

  return (
    <>
      <TableRow hover tabIndex={-1} role="checkbox" selected={selected}>
        <TableCell component="th" scope="row" padding="none">
          <Stack sx={{ ml: 2 }} direction="row" alignItems="center" spacing={2}>
            <Avatar alt={circleName} src={circleImage} />
            <Typography variant="subtitle2" noWrap>
              {circleName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>{fullName}</TableCell>
        <TableCell align="center">
          <Tooltip title="More Info">
            <IconButton onClick={handleShowPartialUserProfile}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <AcceptBtn
            disabled={status === "Approved" || status === "Rejected"}
            onClick={handleApproveJoinRequest}
          />
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <DenyBtn
            disabled={status === "Rejected" || status === "Approved"}
            requestId={requestId ?? ""}
            onClick={handleDenyJoinRequest}
          />
        </TableCell>
      </TableRow>

      <PartialUserProfileModal
        open={showPartialUserProfileModal}
        handleClose={() => setShowPartialUserProfileModal(false)}
        onApprove={handleApproveJoinRequest}
        onDenyOrReject={handleDenyJoinRequest}
        title={`More Details`}
        firstName={firstName}
        lastName={lastName}
        email={email}
        profileImage={profileImage}
        reason={reason}
        rating={rating}
      />
    </>
  );
};

JoinRequestTableRow.prototype = {
  profileImage: PropTypes.string,
  circleImage: PropTypes.string,
  circleName: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  selected: PropTypes.any,
  accept: PropTypes.any,
  deny: PropTypes.any,
  isVerified: PropTypes.any,
  status: PropTypes.any,
  loading: PropTypes.bool,
  isNew: PropTypes.bool,
  onAcceptJoinRequest: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  reason: PropTypes.string,
  rating: PropTypes.number,
};

interface AcceptBtnProps {
  onClick: (e: any) => void;
  disabled?: any;
}

function AcceptBtn(props: AcceptBtnProps) {
  const { onClick, disabled } = props;
  const [showLoading, setShowLoading] = useState(false);

  const handleClick = async (e: any) => {
    setShowLoading(true);
    try {
      await onClick(e);
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <AcceptButton
        disabled={disabled}
        onClick={handleClick}
        variant="contained"
        size="small"
      >
        {showLoading ? (
          <LoadingSpinner
            loading={showLoading}
            sx={{ color: colors.white }}
            size={30}
            color={colors.white}
          />
        ) : (
          "Accept"
        )}
      </AcceptButton>
    </Stack>
  );
}

AcceptBtn.propType = {
  onClick: PropTypes.func,
  disabled: PropTypes.any,
};

interface DenyBtnProps {
  requestId: string;
  onClick?: any;
  disabled?: any;
  handleOpenNotificationModal?: any;
}

function DenyBtn(props: DenyBtnProps) {
  const { requestId, onClick, disabled } = props;
  const [showLoading, setShowLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleDeny = async () => {
    try {
      setShowLoading(true);
      //   const response = await dispatch(denyJoinRequest(requestId)).unwrap();
      // await dispatch(fetchAddRequest(userData?._id ?? ""));
      //   if (response) {
      //     setShowLoading(false);
      //   }
      if (onClick) {
        onClick();
      }
    } catch (error) {
      setShowLoading(false);
    }
  };

  return (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <DenyButton
        disabled={disabled}
        onClick={handleDeny}
        variant="contained"
        size="small"
      >
        {showLoading ? (
          <LoadingSpinner
            loading={showLoading}
            sx={{ color: colors.white }}
            size={30}
            color={colors.white}
          />
        ) : (
          "Deny"
        )}
      </DenyButton>
    </Stack>
  );
}

DenyBtn.propType = {
  onClick: PropTypes.func,
  disabled: PropTypes.any,
  handleOpenNotificationModal: PropTypes.any,
};

export default JoinRequestTableRow;
